import { useMemo, useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/** A single photo-gallery item, as populated by the property feed. */
interface GalleryImage {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

/** How long each slide stays on screen before crossfading to the next. */
const SLIDE_INTERVAL_MS = 6000;

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
}

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  ctaLabel = 'Back To Home',
  ctaUrl = '/',
  // Transparent "button" CTA centered in the hero box. Defaults route visitors
  // to the Floor Plans page; the Floor Plans page itself overrides these props
  // (via pages.json) to point at Amenities instead.
  secondaryCtaLabel = 'Find Your Space',
  secondaryCtaUrl = '/floor-plans',
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const secondaryCtaPath = `${prefix}.secondary_cta`;
  const bgImagePath = `${prefix}.bg_image`;
  const slidesPath = `${prefix}.slides`;

  // Rendered as a "← Back To Home" text link sitting top-right on the title
  // row (not a button over the image). Defaults to home but stays editable per
  // page; property_apply_now_url / property_website_url remain available to
  // editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  // Boxed hero image, mirroring the homepage Hero's editable fallback image.
  // Defaults to the property featured image so the box is never blank, and
  // remains editable per page via the `${prefix}.bg_image` binding.
  const bgImageDef: SlotGroupDefinition = {
    label: 'Hero Image',
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: 'property_featured_image',
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultValue: 'Page hero image',
      },
    },
  };

  // Transparent call-to-action button overlaid in the hero box. Editable per
  // page; the URL/label defaults come from props so each page can point it at
  // the most relevant next step.
  const secondaryCtaDef: SlotGroupDefinition = {
    label: 'Hero Button',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: secondaryCtaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Button Text',
        type: 'text',
        defaultValue: secondaryCtaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  const secondaryCta = useSlotGroup(secondaryCtaPath, secondaryCtaDef);
  const bgImage = useSlotGroup(bgImagePath, bgImageDef);

  // Slideshow images pulled from the property photo gallery, mirroring the
  // homepage Hero. The editor can reorder / hide / inject slides through this
  // binding.
  const gallerySlides = useCollectionBinding<GalleryImage>(slidesPath, {
    collection: 'photo_gallery',
    defaultLimit: 6,
  });

  // Build the slide list. Prefer the gallery binding; fall back to the single
  // editable hero image so the box is never blank when no gallery is set.
  const slides = useMemo(() => {
    const fromGallery = gallerySlides
      .map((g) => ({
        url: g.image || g.original_url || '',
        alt: g.alt_text || g.title || bgImage.alt || '',
      }))
      .filter((s) => s.url);
    if (fromGallery.length > 0) return fromGallery;
    if (bgImage.image) return [{ url: bgImage.image, alt: bgImage.alt || '' }];
    return [];
  }, [gallerySlides, bgImage.image, bgImage.alt]);

  const [current, setCurrent] = useState(0);
  // The slide we just left. It stays fully opaque *beneath* the incoming slide
  // during the crossfade so the black box background never flashes through.
  const [prev, setPrev] = useState<number | null>(null);
  const slideCount = slides.length;

  // Auto-advance the slideshow. Pauses when only one slide exists.
  useEffect(() => {
    if (slideCount <= 1) return;
    const id = setInterval(() => {
      setCurrent((c) => {
        setPrev(c);
        return (c + 1) % slideCount;
      });
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [slideCount]);

  // Clamp the active index in case the slide list shrinks underneath us.
  const safeCurrent = slideCount > 0 ? Math.min(current, slideCount - 1) : 0;

  return (
    <section
      className="bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 pb-10 md:pb-14"
      // The fixed Header already publishes a layout spacer equal to its full
      // height (offer banner + nav bar), so the hero must NOT re-add
      // --offer-banner-height here or the banner height is counted twice. A
      // small constant gives the title breathing room below the nav, matching
      // the tight spacing of the Home Hero (which uses pt-0 + the same spacer).
      style={{ paddingTop: '1.5rem' }}
    >
      <div className="w-full">

        {/* Title row — page title on the left, a "← Back To Home" style text
            link on the right. Stacks on small screens. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-5">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-solid-title text-xl sm:text-2xl md:text-3xl font-bold tracking-wider leading-tight break-words text-left text-[var(--vibe-color-text)] m-0"
          />
          <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
            <a
              href={cta.url || '/'}
              target={cta._target || '_self'}
              className="hero-back-link group/cta inline-flex items-center gap-2 whitespace-nowrap font-heading text-sm md:text-base tracking-wider text-[var(--vibe-color-text)] transition-opacity hover:opacity-70"
            >
              <span aria-hidden className="transition-transform duration-300 group-hover/cta:-translate-x-1">←</span>
              <span>{cta.label || ctaLabel}</span>
            </a>
          </EditableSlotGroup>
        </div>

        {/* Boxed slideshow with the blurb centered on top */}
        <div className="hero-solid-box relative w-full overflow-hidden">
          {/* Slide layer (editable). The featured-image slot group remains the
              editable fallback slide; the gallery binding supplies the rest.
              All slides render up front so the box never flashes empty between
              crossfades. */}
          <EditableSlotGroup
            groupPath={bgImagePath}
            definition={bgImageDef}
            className="absolute inset-0"
          >
            {slideCount > 0 ? (
              slides.map((slide, i) => (
                <div
                  key={slide.url || i}
                  className={`hero-solid-slide${i === safeCurrent ? ' is-active' : i === prev ? ' is-prev' : ''}`}
                  aria-hidden={i === safeCurrent ? undefined : true}
                >
                  <VibesitesImage
                    url={slide.url}
                    alt={slide.alt || ''}
                    priority={i === 0}
                    sizes="100vw"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))
            ) : (
              <div className="hero-solid-slide is-active">
                <div className="w-full h-full bg-primary" />
              </div>
            )}
          </EditableSlotGroup>

          {/* Gradient overlay for text contrast — decorative, non-interactive. */}
          <div className="absolute inset-0 z-[3] bg-gradient-to-t from-black/80 via-black/65 to-black/55 pointer-events-none" />

          {/* Content overlay: blurb + transparent CTA button, centered. */}
          <div className="relative z-10 flex flex-col items-center justify-center gap-5 h-full text-center text-white px-6 sm:px-10">
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="font-body text-xs sm:text-sm md:text-base leading-relaxed max-w-3xl mx-auto drop-shadow-md [&_a]:underline"
            />
            <EditableSlotGroup groupPath={secondaryCtaPath} definition={secondaryCtaDef}>
              <a
                href={secondaryCta.url || secondaryCtaUrl}
                target={secondaryCta._target || '_self'}
                className="hero-solid-cta inline-flex items-center whitespace-nowrap font-heading text-xs sm:text-sm md:text-base tracking-wider capitalize border border-white/80 px-6 py-3 text-white transition-colors duration-300 hover:bg-white hover:text-[var(--vibe-color-text)]"
              >
                <span>{secondaryCta.label || secondaryCtaLabel}</span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>

      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.secondary_cta"
        definition={secondaryCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Boxed hero frame — shorter than the homepage Hero box
           (clamp(440px, 78vh, 880px)) so subpages get a lighter banner. */
        .hero-solid-box {
          height: clamp(260px, 42vh, 460px);
          background: #000;
        }
        /* Crossfading slideshow slides, mirroring the homepage Hero. */
        .hero-solid-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          z-index: 0;
          transition: opacity 1.1s ease-in-out;
          will-change: opacity;
        }
        /* Outgoing slide: stays fully opaque beneath the incoming one so the
           crossfade never reveals the black background. */
        .hero-solid-slide.is-prev {
          opacity: 1;
          z-index: 1;
          transition: none;
        }
        /* Incoming slide: fades in on top of the still-opaque previous slide. */
        .hero-solid-slide.is-active {
          opacity: 1;
          z-index: 2;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-solid-slide { transition: none; }
        }
        /* Borrow the PropertyOverview section-title face (slab/western
           display) for the hero page title. */
        .hero-solid-title {
          font-family: "Josefin Slab", Rockwell, Georgia, serif;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'Back To Home',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'herosolidbg.secondary_cta': {
      label: 'Hero Button',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: 'Find Your Space',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'herosolidbg.bg_image': {
      label: 'Hero Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
    },
  },
  collectionBindings: {
    'herosolidbg.slides': {
      label: 'Slideshow Images',
      collection: 'photo_gallery',
      defaultLimit: 6,
      description:
        'Images cycled in the boxed interior-page hero slideshow. Pulled from the property photo gallery; falls back to the hero image when empty.',
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
